// vocabularyStorage.js - Updated to handle vocabulary progress with localStorage for multiple courses
// Enhanced version with course-specific session saving/restoration

/**
 * Utility class to manage vocabulary session storage using localStorage
 * Now supports multiple courses (ASA101, ASA103, etc.)
 */
export class VocabularyStorage {
  /**
   * Save the current quiz session state to allow resuming later
   * @param {number} subcategoryId - The ID of the vocabulary subcategory
   * @param {object} sessionState - Current quiz session state
   * @param {string} coursePrefix - Course prefix (e.g., 'asa101_vocab', 'asa103_vocab')
   */
  static saveQuizSession(subcategoryId, sessionState, coursePrefix = 'asa101_vocab') {
    try {
      const key = `${coursePrefix}_session_${subcategoryId}`;
      localStorage.setItem(key, JSON.stringify({
        ...sessionState,
        timestamp: new Date().toISOString()
      }));
      
      // Update last active subcategory for this course
      localStorage.setItem(`${coursePrefix}_last_subcategory`, subcategoryId.toString());
    } catch (error) {
      console.error('Error saving quiz session:', error);
    }
  }
  
  /**
   * Load a saved quiz session for a specific subcategory
   * @param {number} subcategoryId - The ID of the vocabulary subcategory
   * @param {string} coursePrefix - Course prefix (e.g., 'asa101_vocab', 'asa103_vocab')
   * @returns {object|null} - The saved session or null if not found
   */
  static loadQuizSession(subcategoryId, coursePrefix = 'asa101_vocab') {
    try {
      const key = `${coursePrefix}_session_${subcategoryId}`;
      const savedData = localStorage.getItem(key);
      return savedData ? JSON.parse(savedData) : null;
    } catch (error) {
      console.error('Error loading quiz session:', error);
      return null;
    }
  }
  
  /**
   * Get the last active vocabulary subcategory for a specific course
   * @param {string} coursePrefix - Course prefix (e.g., 'asa101_vocab', 'asa103_vocab')
   * @returns {number|null} - The ID of the last subcategory or null
   */
  static getLastActiveSubcategory(coursePrefix = 'asa101_vocab') {
    try {
      const subcategoryId = localStorage.getItem(`${coursePrefix}_last_subcategory`);
      return subcategoryId ? parseInt(subcategoryId, 10) : null;
    } catch (error) {
      console.error('Error getting last active subcategory:', error);
      return null;
    }
  }
  
  /**
   * Clear a specific quiz session
   * @param {number} subcategoryId - The ID of the vocabulary subcategory
   * @param {string} coursePrefix - Course prefix (e.g., 'asa101_vocab', 'asa103_vocab')
   */
  static clearQuizSession(subcategoryId, coursePrefix = 'asa101_vocab') {
    try {
      const key = `${coursePrefix}_session_${subcategoryId}`;
      localStorage.removeItem(key);
      
      // If this was the last active subcategory, clear that too
      const lastActive = this.getLastActiveSubcategory(coursePrefix);
      if (lastActive === subcategoryId) {
        localStorage.removeItem(`${coursePrefix}_last_subcategory`);
      }
    } catch (error) {
      console.error('Error clearing quiz session:', error);
    }
  }
  
  /**
   * Clear all vocabulary progress data for a specific course
   * @param {string} coursePrefix - Course prefix (e.g., 'asa101_vocab', 'asa103_vocab')
   */
  static clearAllProgress(coursePrefix = 'asa101_vocab') {
    try {
      // Get all keys in localStorage
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(coursePrefix)) {
          keys.push(key);
        }
      }
      
      // Remove all course-specific vocabulary keys
      keys.forEach(key => localStorage.removeItem(key));
      
      console.log(`All ${coursePrefix} progress cleared successfully`);
    } catch (error) {
      console.error('Error clearing vocabulary progress:', error);
    }
  }
  
  /**
   * Clear ALL vocabulary progress data for ALL courses
   */
  static clearAllCoursesProgress() {
    try {
      // Get all keys in localStorage
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('_vocab_') || key.includes('vocab_'))) {
          keys.push(key);
        }
      }
      
      // Remove all vocabulary-related keys
      keys.forEach(key => localStorage.removeItem(key));
      
      console.log('All vocabulary progress for all courses cleared successfully');
    } catch (error) {
      console.error('Error clearing all vocabulary progress:', error);
    }
  }
  
  /**
   * Get progress summary for a specific course
   * @param {string} coursePrefix - Course prefix (e.g., 'asa101_vocab', 'asa103_vocab')
   * @returns {object} - Progress summary with completed sessions and total subcategories
   */
  static getProgressSummary(coursePrefix = 'asa101_vocab') {
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`${coursePrefix}_session_`)) {
          keys.push(key);
        }
      }
      
      const sessions = keys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          const subcategoryId = parseInt(key.split('_').pop());
          return {
            subcategoryId,
            ...data
          };
        } catch {
          return null;
        }
      }).filter(Boolean);
      
      return {
        totalSessions: sessions.length,
        sessions: sessions.sort((a, b) => a.subcategoryId - b.subcategoryId)
      };
    } catch (error) {
      console.error('Error getting progress summary:', error);
      return { totalSessions: 0, sessions: [] };
    }
  }
}

/**
 * Auto-save helper for quiz integration - Updated for multi-course support
 */
export class QuizAutoSave {
  /**
   * Initialize auto-save for a quiz session
   * @param {number} subcategoryId - The subcategory ID
   * @param {object} initialState - Initial quiz state
   * @param {string} selectedCourse - Course code (e.g., 'ASA101', 'ASA103')
   */
  constructor(subcategoryId, initialState = null, selectedCourse = 'ASA101') {
    this.subcategoryId = subcategoryId;
    this.selectedCourse = selectedCourse;
    this.coursePrefix = `${selectedCourse.toLowerCase()}_vocab`;
    this.lastSaveTime = Date.now();
    this.saveInterval = 10000; // Save every 10 seconds
    this.autoSaveTimer = null;
    
    // Load existing session if available and no initial state provided
    if (!initialState) {
      const savedSession = VocabularyStorage.loadQuizSession(subcategoryId, this.coursePrefix);
      this.currentState = savedSession || {
        currentQuestionIndex: 0,
        answers: [],
        startTime: new Date().toISOString(),
        course: selectedCourse
      };
    } else {
      this.currentState = {
        ...initialState,
        course: selectedCourse
      };
    }
    
    // Start auto-save timer
    this.startAutoSave();
  }
  
  /**
   * Start the auto-save timer
   */
  startAutoSave() {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }
    
    this.autoSaveTimer = setInterval(() => {
      this.saveSession();
    }, this.saveInterval);
  }
  
  /**
   * Stop the auto-save timer
   */
  stopAutoSave() {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }
  
  /**
   * Update the current state and trigger an immediate save
   * @param {object} newState - New state data to merge with current state
   */
  updateState(newState) {
    this.currentState = {
      ...this.currentState,
      ...newState,
      lastModified: new Date().toISOString(),
      course: this.selectedCourse
    };
    
    this.saveSession();
  }
  
  /**
   * Save the current session state
   */
  saveSession() {
    VocabularyStorage.saveQuizSession(this.subcategoryId, this.currentState, this.coursePrefix);
    this.lastSaveTime = Date.now();
  }
  
  /**
   * Complete the session and clear the saved state
   */
  completeSession() {
    this.stopAutoSave();
    VocabularyStorage.clearQuizSession(this.subcategoryId, this.coursePrefix);
  }
  
  /**
   * Get the current state
   * @returns {object} - Current session state
   */
  getState() {
    return this.currentState;
  }
  
  /**
   * Get the course prefix being used
   * @returns {string} - Course prefix
   */
  getCoursePrefix() {
    return this.coursePrefix;
  }
}